import { useCallback, useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { listProposals, type Proposal } from './api'
import { PROPOSAL_STATUS_LABEL } from './vocab'
import { ProposalCard } from './components/ProposalCard'

const TABS = ['PROPOSED', 'APPROVED', 'APPLIED', 'REJECTED', 'EXPIRED']

/**
 * The inbox — the only channel through which anything here reaches the platform.
 *
 * Two gates and two people: `advisor:approve` accepts a recommendation,
 * `advisor:apply` carries it out, and applying additionally needs the authority
 * the change itself requires. The card says which one is missing rather than
 * failing on the press.
 */
export function ProposalsPage() {
  const [status, setStatus] = useState<string>('PROPOSED')
  const [proposals, setProposals] = useState<Proposal[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setProposals(await listProposals(status, 0, 60))
    } catch (err) {
      setError(explain(err))
    }
  }, [status])

  useEffect(() => { void load() }, [load])

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-erp-lg font-semibold">Proposals</h1>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          Nothing a model recommends changes anything on its own. With one exception — a content
          brief, which generates media inside this module — applying records the decision and
          hands off to the screen that owns the change, under that screen&rsquo;s own authority.
        </p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {TABS.map((tab) => (
          <button key={tab} type="button" onClick={() => setStatus(tab)}
            className={cn('erp-btn h-8 px-2.5 text-erp-xs',
              status === tab && 'border-chocolate text-chocolate')}>
            {PROPOSAL_STATUS_LABEL[tab] ?? tab}
          </button>
        ))}
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {proposals === null && !error && <LoadingBlock />}

      {proposals && proposals.length === 0 && (
        <Note tone="info">
          Nothing {PROPOSAL_STATUS_LABEL[status]?.toLowerCase() ?? status.toLowerCase()}.
        </Note>
      )}

      <ul className="space-y-2">
        {(proposals ?? []).map((proposal) => (
          <ProposalCard key={proposal.id} proposal={proposal} onChanged={() => void load()} />
        ))}
      </ul>
    </div>
  )
}
